/**
 * MessageRenderers — Shared full-fidelity email rendering primitives.
 *
 * Houses the components used by both the `Message` tab (MessageView.tsx) and
 * the customer-history drill-down (CompleteHistoryTab.tsx) so the rendering
 * policy stays consistent across surfaces.
 *
 * Exports:
 *   - HtmlEmailFrame: sandboxed iframe for HTML email bodies
 *   - AttachmentChip: inline attachment chip with click-to-preview
 *   - AttachmentPreviewModal: full-screen preview modal for an attachment
 *   - getAttachmentIcon / formatSize: small shared helpers
 *
 * Task 3201b: extracted from MessageView.tsx to enable reuse without
 * duplicating implementation.
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { Download, FileText, Image, Film, File, X, ExternalLink } from 'lucide-react'
import type { MessageAttachment } from '../types'
import { applyBlanketOverride } from '../lib/ensureLegibleText'

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Map content_type to a display-friendly icon component. */
export function getAttachmentIcon(contentType?: string): ReactNode {
  const ct = contentType ?? ''
  if (ct.startsWith('image/')) return <Image size={14} />
  if (ct.startsWith('video/')) return <Film size={14} />
  if (ct.includes('pdf')) return <FileText size={14} />
  return <File size={14} />
}

/** Format bytes to human-readable size. */
export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ── HtmlEmailFrame — sandboxed iframe for HTML email rendering ───────────────

export interface HtmlEmailFrameProps {
  html: string
  /** Optional style overrides for the iframe (e.g. smaller minHeight for inline use). */
  style?: CSSProperties
}

export function HtmlEmailFrame({ html, style }: HtmlEmailFrameProps): ReactNode {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const writeContent = useCallback(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    const doc = iframe.contentDocument
    if (!doc) return

    // Write the HTML email into the iframe with a base reset stylesheet.
    doc.open()
    doc.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>
          /* Layout / responsive reset */
          * { box-sizing: border-box; }
          html, body {
            margin: 0;
            padding: 12px 16px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            font-size: 14px;
            line-height: 1.6;
            overflow-x: hidden;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          img {
            max-width: 100%;
            height: auto;
            display: inline-block;
          }
          table {
            max-width: 100% !important;
            width: auto !important;
          }
          div, td, th {
            max-width: 100% !important;
          }
          blockquote {
            margin: 8px 0;
            padding: 4px 12px;
            border-left: 3px solid #ccc;
          }
        </style>
      </head>
      <body>${html}</body>
      </html>
    `)
    doc.close()

    // Guarantee legibility regardless of what colour declarations the email
    // HTML contains. The blanket override injects a !important stylesheet
    // directly into doc.head — no getComputedStyle() call required — so it
    // works reliably in WKWebView (Tauri) where computed styles are frequently
    // empty immediately after doc.write(). This is the authoritative legibility
    // mechanism; the previous ensureLegibleText / getComputedStyle approach was
    // unreliable and repeatedly allowed white-on-white content to survive.
    applyBlanketOverride(doc)

    // Disable all links from navigating away.
    doc.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement
      const anchor = target.closest('a')
      if (anchor) {
        e.preventDefault()
        const href = anchor.getAttribute('href')
        if (href && href !== '#') {
          window.open(href, '_blank', 'noopener,noreferrer')
        }
      }
    })
  }, [html])

  useEffect(() => {
    writeContent()
  }, [writeContent])

  return (
    <iframe
      ref={iframeRef}
      style={{ ...defaultStyles.iframe, ...style }}
      sandbox="allow-same-origin"
      title="Email message content"
    />
  )
}

// ── AttachmentChip ───────────────────────────────────────────────────────────

export interface AttachmentChipProps {
  attachment: MessageAttachment
  onPreview: (attachment: MessageAttachment) => void
}

export function AttachmentChip({ attachment, onPreview }: AttachmentChipProps): ReactNode {
  return (
    <button
      type="button"
      style={defaultStyles.attachmentChip}
      onClick={() => onPreview(attachment)}
      title={`${attachment.filename} (${formatSize(attachment.size)})`}
    >
      {getAttachmentIcon(attachment.content_type)}
      <span style={defaultStyles.attachmentFilename}>{attachment.filename}</span>
      <span style={defaultStyles.attachmentSize}>{formatSize(attachment.size)}</span>
      <Download size={12} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
    </button>
  )
}

// ── AttachmentPreviewModal ───────────────────────────────────────────────────

export interface AttachmentPreviewModalProps {
  attachment: MessageAttachment
  onClose: () => void
}

export function AttachmentPreviewModal({
  attachment,
  onClose,
}: AttachmentPreviewModalProps): ReactNode {
  const [textContent, setTextContent] = useState<string | null>(null)
  const [textLoading, setTextLoading] = useState(false)

  const isImage = attachment.content_type?.startsWith('image/')
  const isText = /^text\/|\/(json|xml|csv|log)/.test(attachment.content_type ?? '')
    || /\.(log|txt|csv|json|xml|yml|yaml|md)$/i.test(attachment.filename ?? '')
  const isPdf = attachment.content_type?.includes('pdf')

  // Fetch text content for previewable text files
  useEffect(() => {
    if (!isText || !attachment.url) return
    setTextLoading(true)
    fetch(attachment.url)
      .then((res) => res.text())
      .then((text) => {
        // Limit preview to ~200KB to avoid perf issues
        setTextContent(text.length > 200_000 ? text.slice(0, 200_000) + '\n\n… (truncated)' : text)
      })
      .catch(() => setTextContent('Failed to load file content.'))
      .finally(() => setTextLoading(false))
  }, [isText, attachment.url])

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  const handleDownload = useCallback(() => {
    if (attachment.url) {
      window.open(attachment.url, '_blank', 'noopener,noreferrer')
    }
  }, [attachment.url])

  return (
    <div style={modalStyles.backdrop} onClick={onClose}>
      <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={modalStyles.header}>
          <div style={modalStyles.headerInfo}>
            {getAttachmentIcon(attachment.content_type)}
            <span style={modalStyles.headerFilename}>{attachment.filename}</span>
            <span style={modalStyles.headerSize}>{formatSize(attachment.size)}</span>
          </div>
          <div style={modalStyles.headerActions}>
            {attachment.url && (
              <button
                type="button"
                style={modalStyles.headerBtn}
                onClick={handleDownload}
                title="Open in browser"
              >
                <ExternalLink size={15} />
              </button>
            )}
            <button
              type="button"
              style={modalStyles.headerBtn}
              onClick={onClose}
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={modalStyles.body}>
          {isImage && attachment.url ? (
            <img
              src={attachment.url}
              alt={attachment.filename}
              style={modalStyles.imagePreview}
            />
          ) : isText ? (
            textLoading ? (
              <div style={modalStyles.loadingText}>Loading…</div>
            ) : (
              <pre style={modalStyles.textPreview}>{textContent}</pre>
            )
          ) : isPdf && attachment.url ? (
            <iframe
              src={attachment.url}
              title={attachment.filename}
              style={modalStyles.pdfPreview}
            />
          ) : (
            <div style={modalStyles.fallback}>
              <File size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
              <p style={modalStyles.fallbackFilename}>{attachment.filename}</p>
              <p style={modalStyles.fallbackMeta}>
                {attachment.content_type} · {formatSize(attachment.size)}
              </p>
              {attachment.url && (
                <button
                  type="button"
                  style={modalStyles.downloadBtn}
                  onClick={handleDownload}
                >
                  <Download size={14} />
                  Download
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const defaultStyles: Record<string, CSSProperties> = {
  iframe: {
    width: '100%',
    border: 'none',
    display: 'block',
    background: '#ffffff',
    minHeight: '300px',
    height: '50vh',
    borderRadius: '8px',
  },
  attachmentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 10px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    maxWidth: '220px',
  },
  attachmentFilename: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
    fontWeight: 500,
  },
  attachmentSize: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
}

const modalStyles: Record<string, CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '24px',
  },
  container: {
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '900px',
    maxHeight: '85vh',
    width: '100%',
    overflow: 'hidden',
    boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    flexShrink: 0,
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    flex: 1,
    color: 'var(--text-secondary)',
  },
  headerFilename: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSize: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0,
    marginLeft: '12px',
  },
  headerBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-muted)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imagePreview: {
    maxWidth: '100%',
    maxHeight: '75vh',
    objectFit: 'contain',
    display: 'block',
    margin: '0 auto',
    padding: '16px',
  },
  textPreview: {
    width: '100%',
    margin: 0,
    padding: '16px 20px',
    fontSize: '12px',
    lineHeight: 1.6,
    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
    color: 'var(--text-primary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflow: 'auto',
    maxHeight: '75vh',
  },
  pdfPreview: {
    width: '100%',
    height: '75vh',
    border: 'none',
  },
  loadingText: {
    padding: '40px',
    fontSize: '13px',
    color: 'var(--text-muted)',
  },
  fallback: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    textAlign: 'center',
  },
  fallbackFilename: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    wordBreak: 'break-word',
  },
  fallbackMeta: {
    margin: '6px 0 20px',
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  downloadBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-primary)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  },
}
